/**
 * AfterPay should be added to enable payment on installment basis for a particular price range
 *
 * @module views/components/AfterPay
 * @memberof -Common
 */
import './AfterPay.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import JSscriptTag from '@ulta/core/components/JSscriptTag/JSscriptTag';
import { useListenForGlobal } from '@ulta/core/hooks/useListenForGlobal/useListenForGlobal';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { formatLocale, getApplicationLocale } from '@ulta/core/utils/locale/locale';

/**
 * Represents AfterPay component
 *
 * @method
 * @param {AfterPayProps} props - React properties passed from composition
 * @returns AfterPay
 */
export const AfterPay = function( props ){
  const userLocale = formatLocale( getApplicationLocale( props.locale, isServer() ) );
  const { product, minThreshold, onSdkReady } = props;

  const placementRef = useRef( null );

  useListenForGlobal( {
    watch: () => placementRef.current?.shadowRoot?.childElementCount > 0 ||
               placementRef.current?.childElementCount > 0,
    callback: () => onSdkReady?.()
  } );

  if( !product ){
    onSdkReady?.();
    return;
  }

  const { afterpayMpid, afterpayPlacementId, afterpaySdkSrc } = product;

  if(
    !afterpayMpid ||
    !afterpayPlacementId ||
    !afterpaySdkSrc
  ){
    onSdkReady?.();
    return;
  }

  return (
    <>
      <div className='AfterPay'>
        <JSscriptTag
          tagId='AfterPay'
          src={ afterpaySdkSrc }
          dataSet={ [{ 'analyticsEnabled':'true' }, { 'min': minThreshold }] }
          async={ true }
          immediate={ true }
        />

        <square-placement
          ref={ placementRef }
          data-mpid={ afterpayMpid }
          data-placement-id={ afterpayPlacementId }
          data-page-type='product'
          data-is-eligible={ product.eligible ? 'true' : 'false' }
          data-consumer-locale={ userLocale }
          data-currency={ product.currency }
          data-amount={ product.price }
          data-show-upper-limit='false'
        >
        </square-placement>
      </div>
    </>
  );
};

/**
 * Property type definitions
 * @typedef AfterPayProps
 * @type {object}
 * @property {object} product - Sets the currency, amount, eligibility
 * @property {string} locale - Sets the data-locale field
 * @property {string} minThreshold - Sets minimum purchase amount for AfterPay in sdk
 * @property {string} afterPayMpid - sets afterPayMpid
 * @property {string} afterPayPlacementId - sets afterPayPlacementId
 * @property {string} afterPaySdkSrc - afterPaySdkSrc
 */

export const propTypes =  {
  /** Sets the currency, eligibility, and amount label to display */
  product: PropTypes.object,
  /** Sets the data-locale prop */
  locale: PropTypes.string,
  /** Sets minimum purchase amount for AfterPay in sdk */
  minThreshold: PropTypes.string,
  /** sets afterPayMpid */
  afterPayMpid: PropTypes.string,
  /** sets afterPayPlacementId */
  afterPayPlacementId: PropTypes.string,
  /** sets afterPaySdkSrc */
  afterPaySdkSrc: PropTypes.string,
  /** Callback fired when the Square Afterpay placement has rendered content */
  onSdkReady: PropTypes.func
};

AfterPay.propTypes = propTypes;
AfterPay.displayName = 'AfterPay';

export default AfterPay;
